import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { HEARINGSTATUS_CREATED } from '../../../constants/api';
import {
  getAllHearingTypesInitialize,
  getAllInvitationGroupsInitialize,
  getAllInvitationSourcesInitialize,
  getHearingInitialize,
  getInvitationNotificationsForHearingInitialize,
} from '../../../features/actions/api';
import { allCityAreasSelector } from '../../../features/apiFeatures/cityAreaSlice';
import { esdhTemplatesByHearingTypeIdSelector } from '../../../features/apiFeatures/fieldTemplateSlice';
import { hearingByIdSelector } from '../../../features/apiFeatures/hearingSlice';
import { allHearingTypesSelector } from '../../../features/apiFeatures/hearingTypeSlice';
import { allKleHierarchiesByHearingTypeIdSelector } from '../../../features/apiFeatures/kleHierarchySlice';
import { allSubjectAreasSelector } from '../../../features/apiFeatures/subjectAreaSlice';
import { isAppReadySelector } from '../../../features/bootstrapSlice';
import {
  getAttr,
  getEntityId,
  getRelId,
} from '../../../features/helpers/apiHelpers';
import {
  hearingModelSelector,
  initializeState,
  isHearingModelInCreatedStatusSelector,
  isUpdatingHearingSelector,
  pageStepSelector,
  selectedEsdhTemplateIdSelector,
  setHearingModel,
  setPageStep,
  setSelectedEsdhTemplateId,
  showErrorsSelector,
  updateHearingMetaData,
  updateHearingModelAttribute,
  updateHearingModelRelationship,
} from '../../../features/pageFeatures/createHearingPageSlice';
import {
  hearingIdFromRouterStateSelector,
  stepNumberFromRouterStateSelector,
} from '../../../features/routerSlice';
import { hearingStatusByHearingIdSelector } from '../../../features/selectors/compositeSelectors';
import { CreateHearingHeader } from './createHearingPage/CreateHearingHeader';
import { CreateHearingInvitations } from './createHearingPage/CreateHearingInvitations';
import { CreateHearingMetaData } from './createHearingPage/CreateHearingMetaData';
import { CreateHearingWrite } from './createHearingPage/CreateHearingWrite';
import { buildIncludesString } from '../../../utilities/utilityFunctions';
import { hearingIncludes } from '../../../constants/includes';

const CreateHearingPage = () => {
  const dispatch = useDispatch();

  const isAppReady = useSelector(isAppReadySelector);
  const hearingId = useSelector(hearingIdFromRouterStateSelector);
  const stepNumberFromRoute = useSelector(stepNumberFromRouterStateSelector);
  const hearing = useSelector(hearingByIdSelector(hearingId));
  const hearingModelInCreatedStatus = useSelector(
    isHearingModelInCreatedStatusSelector,
  );

  useEffect(() => {
    if (isAppReady) {
      if (hearingId) {
        const includes = buildIncludesString(
          hearingIncludes.contents,
          hearingIncludes.contentTypes,
          hearingIncludes.userHearingRoles,
          hearingIncludes.userInvitationSourceMappings,
          hearingIncludes.companyHearingRoles,
          hearingIncludes.companyIncitationSourceMappings,
        );
        dispatch(getHearingInitialize({ hearingId, includes }));
        dispatch(getInvitationNotificationsForHearingInitialize({ hearingId }));
      }

      dispatch(getAllHearingTypesInitialize());
      dispatch(getAllInvitationSourcesInitialize());
      dispatch(getAllInvitationGroupsInitialize());

      if (stepNumberFromRoute) {
        dispatch(setPageStep(stepNumberFromRoute));
      }
    }

    return function cleanup() {
      dispatch(initializeState());
    };
  }, [dispatch, isAppReady, hearingId, stepNumberFromRoute]);

  useEffect(() => {
    dispatch(setHearingModel(hearing));
  }, [dispatch, hearing]);

  const hearingModel = useSelector(hearingModelSelector);
  const subjectAreas = useSelector(allSubjectAreasSelector);
  const cityAreas = useSelector(allCityAreasSelector);
  const hearingTypes = useSelector(allHearingTypesSelector);
  const hearingStatus = useSelector(
    hearingStatusByHearingIdSelector(getEntityId(hearingModel)),
  );
  const kleHierarchiesDict = useSelector(
    allKleHierarchiesByHearingTypeIdSelector,
  );
  const esdhTemplates = useSelector(
    esdhTemplatesByHearingTypeIdSelector(getRelId(hearingModel, 'hearingType')),
  );
  const selectedEsdhTemplateId = useSelector(selectedEsdhTemplateIdSelector);

  const kleHierarchies = useMemo(
    () => kleHierarchiesDict[getRelId(hearingModel, 'hearingType')],
    [kleHierarchiesDict, hearingModel],
  );

  const stepNumber = useSelector(pageStepSelector);
  const isUpdatingHearing = useSelector(isUpdatingHearingSelector);
  const showErrors = useSelector(showErrorsSelector);
  const isEditMode = getAttr(hearingStatus, 'status') !== HEARINGSTATUS_CREATED;

  const setSelectedEsdhTemplateIdCallback = (value) =>
    dispatch(setSelectedEsdhTemplateId(value));
  const updateHearingModelAttributeCallback = (attribute, value) =>
    dispatch(updateHearingModelAttribute({ attribute, value }));
  const updateHearingModelRelationshipCallback = (relationship, id) =>
    dispatch(updateHearingModelRelationship({ relationship, id }));
  const updateHearingMetaDataCallback = () => dispatch(updateHearingMetaData());

  return (
    <div className="create-hearing">
      <CreateHearingHeader stepNumber={stepNumber} isEditMode={isEditMode} />
      <div>
        {stepNumber === 1 && (
          <CreateHearingMetaData
            subjectAreas={subjectAreas}
            cityAreas={cityAreas}
            hearingTypes={hearingTypes}
            kleHierarchies={kleHierarchies}
            esdhTemplates={esdhTemplates}
            updateHearingModelAttribute={updateHearingModelAttributeCallback}
            updateHearingModelRelationship={
              updateHearingModelRelationshipCallback
            }
            hearingModel={hearingModel}
            updateHearingMetaData={updateHearingMetaDataCallback}
            isUpdatingHearing={isUpdatingHearing}
            hearingModelInCreatedStatus={hearingModelInCreatedStatus}
            setSelectedEsdhTemplateId={setSelectedEsdhTemplateIdCallback}
            selectedEsdhTemplateId={selectedEsdhTemplateId}
            showErrors={showErrors}
            currentHearingStatus={hearingStatus}
          />
        )}
        {stepNumber === 2 && <CreateHearingWrite hearingModel={hearingModel} />}
        {stepNumber === 3 && (
          <CreateHearingInvitations hearingModel={hearingModel} />
        )}
      </div>
    </div>
  );
};

export { CreateHearingPage };
